import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Chip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { api } from '../../api/api';
import toast from 'react-hot-toast';

const SIZE_KEYS = ['hot_s', 'hot_m', 'ice_s', 'ice_m'];
const SIZE_SHORT = { hot_s: '12oz', hot_m: '16oz', ice_s: '16oz', ice_m: '20oz' };
const CATS = [
  { key: 'espresso', label: 'ESPRESSO' },
  { key: 'brewed_coffee', label: 'BREWED' },
  { key: 'tea', label: 'TEA' },
  { key: 'cold_drink', label: 'COLD DRINKS' },
  { key: 'chocolate', label: 'CHOCOLATE' },
];

export default function AdminPriceEditor({ store, userId }) {
  const [menuPrices, setMenuPrices] = useState([]);
  const [menuMasters, setMenuMasters] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [activeCat, setActiveCat] = useState('espresso');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);

  const loadData = useCallback(async () => {
    if (!store?._id) return;
    try {
      const [pr, mm] = await Promise.all([
        api.get(`/menu-prices/${store._id}`),
        api.get('/menu-master/items'),
      ]);
      setMenuPrices(pr.data || []);
      setMenuMasters(mm.data?.items || mm.data || []);
    } catch (_) {}
  }, [store?._id]);

  useEffect(() => { loadData(); }, [loadData]);

  // 생성된 이미지 로드
  useEffect(() => {
    if (!store?._id) return;
    api.get(`/svg/generated/${store._id}`).then(res => {
      setGeneratedImages(res.data?.files || []);
    }).catch(() => {});
  }, [store?._id]);

  const handleEdit = (menuCode, sizeKey, value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    if (cents <= 0) return;
    setPriceEdits(prev => ({ ...prev, [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: cents } }));
  };

  const getCatMenus = () => {
    return menuPrices.filter(mp => {
      const m = menuMasters.find(mm => mm.code === mp.menuCode);
      return m?.category === activeCat;
    }).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  };

  const changeSummary = Object.entries(priceEdits).flatMap(([code, sizes]) => {
    const mp = menuPrices.find(m => m.menuCode === code);
    if (!mp) return [];
    return Object.entries(sizes).filter(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0)
      .map(([k, v]) => ({ menuCode: code, menuName: mp.menuName, sizeKey: k, oldPrice: mp.prices?.[k] || 0, newPrice: v }));
  });

  // 바로 저장 (admin용 — 요청이 아닌 직접 저장)
  const handleSave = async () => {
    if (changeSummary.length === 0) return;
    setSaving(true);
    try {
      const prices = Object.entries(priceEdits).map(([menuCode, prices]) => ({ menuCode, prices }));
      await api.post(`/menu-prices/bulk-update/${store._id}`, {
        prices, userId, reason: 'Admin 가격 직접 수정',
      });
      toast.success(`${changeSummary.length}건 가격 저장 완료 (JPG 자동 생성 중...)`);
      setPriceEdits({});
      loadData();
      // JPG는 bulk-update에서 fire-and-forget으로 자동 생성됨
      setTimeout(() => {
        api.get(`/svg/generated/${store._id}`).then(res => setGeneratedImages(res.data?.files || [])).catch(() => {});
      }, 5000);
    } catch (e) {
      toast.error('저장 실패: ' + (e.response?.data?.message || e.message));
    }
    setSaving(false);
  };

  // 수동 JPG 생성
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/svg/generate/${store._id}`);
      toast.success(`JPG 생성 완료: ${res.data?.screens?.length || 0}개 스크린`);
      setGeneratedImages(res.data?.files || []);
      // 다시 로드
      const imgRes = await api.get(`/svg/generated/${store._id}`);
      setGeneratedImages(imgRes.data?.files || []);
    } catch (e) {
      toast.error('JPG 생성 실패: ' + (e.response?.data?.message || e.message));
    }
    setGenerating(false);
  };

  const catMenus = getCatMenus();
  const fmt = (cents) => (cents / 100).toFixed(2);
  const catCounts = CATS.map(c => ({
    ...c,
    count: menuPrices.filter(mp => menuMasters.find(m => m.code === mp.menuCode)?.category === c.key).length,
  }));

  return (
    <Box>
      {/* 카테고리 + 액션 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
          {catCounts.map(c => (
            <Chip key={c.key} label={`${c.label} (${c.count})`} size="small"
              onClick={() => setActiveCat(c.key)}
              sx={{
                cursor: 'pointer', fontWeight: 600, fontSize: 11,
                background: activeCat === c.key ? '#1B5E3B' : '#f5f5f5',
                color: activeCat === c.key ? '#fff' : '#757575',
              }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {changeSummary.length > 0 && (
            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
              disabled={saving}
              sx={{ fontSize: 11, background: '#1B5E3B', borderRadius: 2 }}>
              {saving ? <CircularProgress size={14} /> : `저장 (${changeSummary.length})`}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={handleGenerate}
            disabled={generating}
            sx={{ fontSize: 11, borderColor: '#551FFF', color: '#551FFF', borderRadius: 2 }}>
            {generating ? <CircularProgress size={14} /> : 'JPG 생성'}
          </Button>
        </Box>
      </Box>

      {/* 가격 테이블 */}
      {menuPrices.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: 13 }}>
          가격 데이터가 없습니다. 상단 "Init Tier" 버튼으로 초기화하세요.
        </Alert>
      ) : (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1B5E3B', color: '#fff' }}>
                <th style={thS}></th>
                <th style={{ ...thS, textAlign: 'left', minWidth: 140 }}>Product</th>
                <th colSpan={2} style={{ ...thS, background: '#C62828' }}>HOT</th>
                <th colSpan={2} style={{ ...thS, background: '#1565C0' }}>ICED</th>
              </tr>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={thS2}></th>
                <th style={thS2}></th>
                <th style={thS2}>12oz</th><th style={thS2}>16oz</th>
                <th style={thS2}>16oz</th><th style={thS2}>20oz</th>
              </tr>
            </thead>
            <tbody>
              {catMenus.map((mp) => {
                const edits = priceEdits[mp.menuCode] || {};
                const hasChange = Object.entries(edits).some(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0);
                return (
                  <tr key={mp._id} style={{ borderBottom: '1px solid #f0f0f0', background: hasChange ? '#FFFDE7' : '#fff' }}>
                    <td style={{ textAlign: 'center', padding: 4, color: '#ddd' }}>
                      <DragIndicatorIcon sx={{ fontSize: 14 }} />
                    </td>
                    <td style={{ padding: '6px 10px', fontWeight: 500, fontSize: 12 }}>{mp.menuName}</td>
                    {SIZE_KEYS.map(k => {
                      const price = mp.prices?.[k];
                      if (price === undefined) return <td key={k} style={{ textAlign: 'center', color: '#ddd' }}>—</td>;
                      const edited = edits[k];
                      const changed = edited !== undefined && edited !== price && edited > 0;
                      return (
                        <td key={k} style={{ textAlign: 'center', padding: 4 }}>
                          <input type="number" step="0.05" defaultValue={fmt(price)}
                            onBlur={e => handleEdit(mp.menuCode, k, e.target.value)}
                            style={{
                              width: 62, padding: '4px 5px', border: `1px solid ${changed ? '#C5A55A' : '#ddd'}`,
                              borderRadius: 4, textAlign: 'center', fontSize: 12, fontFamily: 'inherit',
                              background: changed ? '#F5EDD6' : '#fafafa',
                            }} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {/* 변경 요약 */}
      {changeSummary.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, border: '1.5px solid #C5A55A', borderRadius: 2, background: '#FDF8F0', fontSize: 12 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: '#5D4037', mb: 0.5 }}>
            변경 {changeSummary.length}건
          </Typography>
          {changeSummary.slice(0, 5).map((c, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}>
              <span>{c.menuName} <span style={{ color: '#888' }}>{SIZE_SHORT[c.sizeKey]}</span></span>
              <span>
                <span style={{ color: '#C62828', textDecoration: 'line-through' }}>${fmt(c.oldPrice)}</span>
                {' → '}
                <span style={{ color: '#1B5E3B', fontWeight: 700 }}>${fmt(c.newPrice)}</span>
              </span>
            </Box>
          ))}
          {changeSummary.length > 5 && (
            <Typography sx={{ fontSize: 11, color: '#888' }}>...외 {changeSummary.length - 5}건</Typography>
          )}
        </Box>
      )}

      {/* 생성된 JPG 미리보기 */}
      {generatedImages.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#888', mb: 0.5 }}>
            Generated Menu Boards ({generatedImages.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
            {generatedImages.map((img, i) => (
              <Box key={i} sx={{ flexShrink: 0, width: 160 }}>
                <a href={img.url || `/generated_menus/${store.name}/${img.name || img}`} target="_blank" rel="noreferrer">
                  <img src={img.url || `/generated_menus/${store.name}/${img.name || img}`} alt={img.name || img}
                    style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                </a>
                <Typography sx={{ fontSize: 9, color: '#888', mt: 0.3, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  {typeof img === 'string' ? img : img.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const thS = { padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: 10, letterSpacing: 0.5 };
const thS2 = { padding: '4px 6px', textAlign: 'center', fontSize: 9, color: '#888', fontWeight: 600, background: '#f9f9f9' };
